import { useState, useRef, useCallback } from "react";
import { useRecordContext, useNotify, useRefresh } from "react-admin";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DataObjectIcon from "@mui/icons-material/DataObject";
import { API_URL, getAuthHeaders } from "../../dataProvider";

type UploadStep = "idle" | "requesting-url" | "uploading-r2" | "confirming" | "done" | "error";

type FileFormat = "gltf" | "glb";

interface UploadUrlResponse {
  uploadUrl: string;
  objectKey: string;
  contentType: string;
}

function getFileFormat(file: File): FileFormat | null {
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext === "gltf") return "gltf";
  if (ext === "glb") return "glb";
  return null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ModelUploadProps {
  /** Called after a successful upload confirm (model file or properties-only update). */
  onUploaded?: () => void;
}

export function ModelUpload({ onUploaded }: ModelUploadProps) {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  const [modelFile, setModelFile] = useState<File | null>(null);
  const [propsFile, setPropsFile] = useState<File | null>(null);
  const [step, setStep] = useState<UploadStep>("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const modelInputRef = useRef<HTMLInputElement>(null);
  const propsInputRef = useRef<HTMLInputElement>(null);

  const handleModelFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setModelFile(file);
      setStep("idle");
      setErrorMessage("");
    },
    []
  );

  const handlePropsFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setPropsFile(file);
    },
    []
  );

  const uploadToR2 = useCallback(
    (uploadUrl: string, file: File, contentType: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl);
        // Must match the ContentType that was signed into the pre-signed URL.
        xhr.setRequestHeader("Content-Type", contentType);

        // Abort and reject if upload makes no progress for 30 seconds.
        const STALL_MS = 30_000;
        let stallTimer: ReturnType<typeof setTimeout> | null = null;
        let rejected = false;

        const rejectOnce = (err: Error) => {
          if (!rejected) { rejected = true; reject(err); }
        };

        const resetStallTimer = () => {
          if (stallTimer) clearTimeout(stallTimer);
          stallTimer = setTimeout(() => {
            xhr.abort();
            rejectOnce(new Error(
              "Caricamento bloccato: nessun progresso per 30 secondi. " +
              "Verifica che il server sia in esecuzione e raggiungibile."
            ));
          }, STALL_MS);
        };

        resetStallTimer();

        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
            resetStallTimer();
          }
        });

        xhr.addEventListener("load", () => {
          if (stallTimer) clearTimeout(stallTimer);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            rejectOnce(new Error(
              `Storage ha risposto con stato ${xhr.status}` +
              (xhr.responseText ? `: ${xhr.responseText.slice(0, 200)}` : "")
            ));
          }
        });

        xhr.addEventListener("error", () => {
          if (stallTimer) clearTimeout(stallTimer);
          rejectOnce(new Error(
            "Errore di rete durante il caricamento. " +
            "Verifica che il server sia raggiungibile."
          ));
        });

        xhr.addEventListener("abort", () => {
          if (stallTimer) clearTimeout(stallTimer);
        });

        xhr.send(file);
      });
    },
    []
  );

  const handleUpload = useCallback(async () => {
    if (!record) return;

    const existingObjectKey = record.r2_object_key as string | null;
    const existingFileFormat = record.file_format as string | null;

    // JSON-only update: skip the R2 upload and re-confirm with the existing key
    const propsOnlyMode = !modelFile && !!propsFile && !!existingObjectKey;

    if (!modelFile && !propsOnlyMode) {
      notify("Seleziona prima un file 3D", { type: "warning" });
      return;
    }

    setErrorMessage("");
    setUploadProgress(0);

    try {
      let objectKey: string;
      let fileFormat: string;

      if (propsOnlyMode) {
        objectKey = existingObjectKey!;
        fileFormat = existingFileFormat!;
      } else {
        const fmt = getFileFormat(modelFile!);
        if (!fmt) {
          notify("Formato file non supportato. Usa .gltf o .glb", { type: "warning" });
          return;
        }
        fileFormat = fmt;

        // Step 1: Request pre-signed upload URL
        setStep("requesting-url");
        const urlResponse = await fetch(
          `${API_URL}/admin/models/${record.id}/file/upload-url`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(getAuthHeaders() as Record<string, string>),
            },
            body: JSON.stringify({ fileName: modelFile!.name, fileFormat }),
          }
        );

        if (!urlResponse.ok) {
          const err = (await urlResponse.json().catch(() => ({}))) as { message?: string };
          throw new Error(err.message ?? `Failed to get upload URL (${urlResponse.status})`);
        }

        const { uploadUrl, objectKey: newKey, contentType } =
          (await urlResponse.json()) as UploadUrlResponse;
        objectKey = newKey;

        // Step 2: Upload directly to R2
        setStep("uploading-r2");
        await uploadToR2(uploadUrl, modelFile!, contentType);
      }

      // Step 3: Parse properties JSON if provided, then confirm
      setStep("confirming");
      let properties: Record<string, unknown> | undefined;

      if (propsFile) {
        const propsText = await propsFile.text();
        try {
          properties = JSON.parse(propsText) as Record<string, unknown>;
        } catch {
          throw new Error(
            "Impossibile analizzare il file JSON delle proprietà — assicurati che sia JSON valido"
          );
        }
      }

      const confirmResponse = await fetch(
        `${API_URL}/admin/models/${record.id}/file/confirm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(getAuthHeaders() as Record<string, string>),
          },
          body: JSON.stringify({
            objectKey,
            fileFormat,
            ...(properties !== undefined ? { properties } : {}),
          }),
        }
      );

      if (!confirmResponse.ok) {
        const err = (await confirmResponse.json().catch(() => ({}))) as { message?: string };
        throw new Error(err.message ?? `Failed to confirm upload (${confirmResponse.status})`);
      }

      setStep("done");
      setUploadProgress(100);
      notify(
        propsOnlyMode ? "Proprietà aggiornate con successo" : "Modello 3D caricato con successo",
        { type: "success" }
      );
      refresh();
      onUploaded?.();

      // Reset file inputs
      setModelFile(null);
      setPropsFile(null);
      if (modelInputRef.current) modelInputRef.current.value = "";
      if (propsInputRef.current) propsInputRef.current.value = "";
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Si è verificato un errore sconosciuto";
      setStep("error");
      setErrorMessage(message);
      notify(`Caricamento fallito: ${message}`, { type: "error" });
    }
  }, [record, modelFile, propsFile, notify, uploadToR2, refresh, onUploaded]);

  const handleReset = useCallback(() => {
    setStep("idle");
    setModelFile(null);
    setPropsFile(null);
    setUploadProgress(0);
    setErrorMessage("");
    if (modelInputRef.current) modelInputRef.current.value = "";
    if (propsInputRef.current) propsInputRef.current.value = "";
  }, []);

  const isUploading =
    step === "requesting-url" ||
    step === "uploading-r2" ||
    step === "confirming";

  const stepLabel: Record<UploadStep, string> = {
    idle: "",
    "requesting-url": "Richiesta URL di caricamento...",
    "uploading-r2": `Caricamento in corso... ${uploadProgress}%`,
    confirming: "Conferma caricamento...",
    done: "Caricamento completato",
    error: "Caricamento fallito",
  };

  if (!record) return null;

  const existingFormat = record.file_format as string | null;
  const existingKey = record.r2_object_key as string | null;
  const existingUploadedAt = record.uploaded_at as string | null;
  const rawFileName = existingKey ? existingKey.split("/").pop() : null;
  const existingFileName = rawFileName ? rawFileName.replace(/^\d+_/, "") : null;
  // properties can arrive wrapped as `{ elements: {...} }` (initial upload) or as
  // the bare element map (viewer property PATCH stores `dataStore.elements` without
  // the wrapper). Mirror normalizeElements in OtherPropertiesPanel and accept both.
  const propsData = record.properties as
    | { elements?: Record<string, unknown> }
    | Record<string, unknown>
    | null;
  const existingElements =
    propsData && typeof propsData === "object"
      ? "elements" in propsData &&
        propsData.elements &&
        typeof propsData.elements === "object"
        ? propsData.elements
        : (propsData as Record<string, unknown>)
      : null;
  const existingElementCount = existingElements
    ? Object.keys(existingElements).length
    : 0;

  return (
    <Box sx={{ mt: 3, width: "100%" }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Caricamento modello 3D
      </Typography>

      {/* Current file info */}
      {existingFormat && existingFileName && (
        <Alert
          severity="info"
          icon={<InsertDriveFileIcon />}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" component="div">
            <strong>File corrente:</strong> {existingFileName}{" "}
            <Chip
              label={existingFormat.toUpperCase()}
              size="small"
              sx={{ ml: 0.5, mr: 1 }}
            />
            {existingUploadedAt && (
              <Typography component="span" variant="caption" color="text.secondary">
                caricato il{" "}
                {new Date(existingUploadedAt).toLocaleDateString("it-IT", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            )}
          </Typography>
        </Alert>
      )}

      {existingElementCount > 0 && (
        <Alert severity="info" icon={<DataObjectIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Dati JSON correnti:</strong>{" "}
            <Chip label={`${existingElementCount} elementi`} size="small" sx={{ ml: 0.5 }} />
          </Typography>
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {existingFormat
          ? "Carica un nuovo file per sostituire quello esistente."
          : "Carica un file modello 3D (GLTF, GLB) e facoltativamente un file JSON delle proprietà per questo modello."}
      </Typography>

      {/* Model file input */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          File modello 3D <span style={{ color: "red" }}>*</span>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={isUploading}
          >
            Scegli file modello
            <input
              ref={modelInputRef}
              type="file"
              accept=".gltf,.glb"
              hidden
              onChange={handleModelFileChange}
            />
          </Button>
          {modelFile && (
            <Chip
              label={`${modelFile.name} (${formatBytes(modelFile.size)})`}
              onDelete={isUploading ? undefined : () => setModelFile(null)}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {/* Properties file input */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          File JSON proprietà{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            (opzionale)
          </Typography>
        </Typography>
        {existingElementCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Caricando un nuovo file JSON sostituirai i dati esistenti.
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            component="label"
            disabled={isUploading}
            size="small"
          >
            Scegli file JSON
            <input
              ref={propsInputRef}
              type="file"
              accept=".json"
              hidden
              onChange={handlePropsFileChange}
            />
          </Button>
          {propsFile && (
            <Chip
              label={`${propsFile.name} (${formatBytes(propsFile.size)})`}
              onDelete={isUploading ? undefined : () => setPropsFile(null)}
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {/* Progress indicator */}
      {isUploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {stepLabel[step]}
          </Typography>
          <LinearProgress
            variant={step === "uploading-r2" ? "determinate" : "indeterminate"}
            value={step === "uploading-r2" ? uploadProgress : undefined}
          />
        </Box>
      )}

      {/* Success state */}
      {step === "done" && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleReset}>
              Carica un altro
            </Button>
          }
        >
          {!modelFile && propsFile
            ? "Proprietà aggiornate con successo."
            : "Modello 3D caricato e confermato con successo."}
        </Alert>
      )}

      {/* Error state */}
      {step === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Action buttons */}
      {step !== "done" && (
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleUpload()}
            disabled={(!modelFile && !(propsFile && record.r2_object_key)) || isUploading}
            startIcon={<CloudUploadIcon />}
          >
            {isUploading
              ? stepLabel[step]
              : !modelFile && propsFile
              ? "Aggiorna proprietà"
              : "Carica modello"}
          </Button>
          {(modelFile || propsFile || step === "error") && !isUploading && (
            <Button variant="outlined" onClick={handleReset}>
              Reimposta
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
